import { Injectable } from '@angular/core';
import { logUserAction } from '@office-start/telemetry-service';
import { Area, Referral } from '@office-start/telemetry-types';
import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents, KnownReferralPlatform } from '../instrumentation/instrumentation.service';
import { FileType } from '../models/file-item.model';
import { CreateDocumentResultType, CreateResultHandler } from './create-document.service';
import { OneDriveGraphService } from './onedrive-graph.service';

@Injectable({
    providedIn: 'root'
})
export class CreateStreamService {
    private oneDriveUrl: string;

    public constructor(
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly oneDriveGraphService: OneDriveGraphService,
        private readonly instrumentationService: InstrumentationService) {
        }

    public createStreamContent(correlationId: string, fileType: FileType, clickLocation?: string, resultHandler?: CreateResultHandler, useNewPlaylistCreationUrl?: boolean) {
        const startTime = Date.now();
        const getLatency = () => Date.now() - startTime;
        if (fileType === FileType.PlayLists || fileType === FileType.PlayList) {
            this.performAction(
                correlationId,
                FileType.PlayList,
                clickLocation || KnownAreas.Templates,
                useNewPlaylistCreationUrl);
            this.instrumentationService.logClickEvent(
                KnownInstrumentationEvents.NewPlaylistButtonClick,
                'CreateNewPlaylist',
                clickLocation || KnownAreas.Templates,
                FileType.PlayList.toString());
            if (resultHandler) {
                resultHandler({ type: CreateDocumentResultType.GoToBlankDoc, latency: getLatency(), platform: KnownReferralPlatform.Web });
            } else {
                logUserAction({ result: 'Refer', target: Referral.PlayLists, area: clickLocation ? clickLocation as Area : 'Templates', id: 'CreateNewPlaylist', eventName: KnownInstrumentationEvents.NewPlaylistButtonClick });
            }
        } else if (fileType === FileType.Stream) {
            this.performAction(
                correlationId,
                FileType.Stream,
                clickLocation || KnownAreas.Templates);
            this.instrumentationService.logClickEvent(
                KnownInstrumentationEvents.NewVideoRecorderButtonClick,
                'CreateNewVideoRecorder',
                clickLocation || KnownAreas.Templates,
                FileType.Stream.toString());
            if (resultHandler) {
                resultHandler({ type: CreateDocumentResultType.GoToBlankDoc, latency: getLatency(), platform: KnownReferralPlatform.Web });
            } else {
                logUserAction({ result: 'Refer', target: Referral.MicrosoftStream, area: clickLocation ? clickLocation as Area : 'Templates', id: 'CreateNewVideoRecorder', eventName: KnownInstrumentationEvents.NewVideoRecorderButtonClick });
            }
        } else if (fileType === FileType.ScreenRecording) {
            this.performAction(
                correlationId,
                FileType.ScreenRecording,
                clickLocation || KnownAreas.Templates);
            this.instrumentationService.logClickEvent(
                KnownInstrumentationEvents.NewScreenRecorderButtonClick,
                'CreateNewScreenRecorder',
                clickLocation || KnownAreas.Templates,
                FileType.Stream.toString());
            if (resultHandler) {
                resultHandler({ type: CreateDocumentResultType.GoToBlankDoc, latency: getLatency(), platform: KnownReferralPlatform.Web });
            } else {
                logUserAction({ result: 'Refer', target: Referral.MicrosoftStream, area: clickLocation ? clickLocation as Area : 'Templates', id: 'CreateNewScreenRecorder', eventName: KnownInstrumentationEvents.NewScreenRecorderButtonClick });
            }
        }
    }

    private async performAction(correlationId: string, fileType: FileType, clickLocation: string, useNewPlaylistCreationUrl?: boolean): Promise<void> {
        try {
            // Set OneDrive URL. If it fails or is not available, we will still try to fetch
            // it through UPA like before.
            const rootUrls = await this.oneDriveGraphService.getMyDriveRootUrl();
            const tenantRoot = rootUrls && rootUrls.tenantRoot;
            const userRoot = rootUrls && rootUrls.userRoot;
            const referrerScenario = clickLocation === 'Templates' ? 'StreamStartPage' : clickLocation;
            if (tenantRoot || userRoot) {
                const documentsIndex = userRoot.lastIndexOf('/Documents');
                if (documentsIndex === -1) {
                    this.instrumentationService.logErrorEvent(
                        KnownInstrumentationEvents.OneDriveUserRootUrl,
                        this.createInstrumentationParams(correlationId, fileType));
                    this.globalErrorHandler.handleError('userRootUrl not in expected format', ErrorId.sCtl9);
                    this.oneDriveUrl = tenantRoot; // use the tenant root as a fallback, should work for most cases
                } else {
                    this.oneDriveUrl = userRoot.substring(0, documentsIndex);
                }
                let href = '';
                switch (fileType) {
                    case FileType.Stream:
                        href = `${this.oneDriveUrl}/_layouts/15/stream.aspx?action=create&referrer=OfficeHome&referrerScenario=${referrerScenario}&cid=${correlationId}`;
                        break;
                    case FileType.ScreenRecording:
                        href = `${this.oneDriveUrl}/_layouts/15/stream.aspx?action=create&referrer=OfficeHome&referrerScenario=${referrerScenario}&captureMode=screenrecorder&cid=${correlationId}`;
                        break;
                    default:
                        if (useNewPlaylistCreationUrl) {
                            href = `${this.oneDriveUrl}/_layouts/15/lists.aspx?openListTemplateId=3a867b4a-7429-0e1a-b02e-bf4b240ff1ce&workload=Stream&cid=${correlationId}`;
                        } else {
                            href = `${this.oneDriveUrl}/_layouts/15/lists.aspx?openCreate=true&workload=Stream&cid=${correlationId}`;
                        }
                        break;
                }
                window.open(href, '_blank');
            }
        } catch (err) {
            this.instrumentationService.logErrorEvent(
                fileType === FileType.PlayLists || fileType === FileType.PlayList ? KnownInstrumentationEvents.NewPlaylistButtonClick : fileType === FileType.ScreenRecording ? KnownInstrumentationEvents.NewScreenRecorderButtonClick : KnownInstrumentationEvents.NewVideoRecorderButtonClick,
                this.createInstrumentationParams(correlationId));
            return this.globalErrorHandler.handleError(err, ErrorId.sCtl9);
        }
    }

    private createInstrumentationParams(
        correlationId: string,
        fileType?: FileType
    ): InstrumentationParams {
        if (fileType) {
            return {
                [InstrumentationParam.ResourceId]: correlationId,
                [InstrumentationParam.DocumentType]: FileType[fileType]
            };
        }

        return {
            [InstrumentationParam.ResourceId]: correlationId
        };
    }
}
